import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { deleteProfile, Profile, Recipient, updateProfile, updateProfileStatus } from '@/lib/forward-api';
import { Edit, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteProfileDialog } from './delete-profile-dialog';
import { EditProfileDialog } from './edit-profile-dialog';
import { ProfileActionsTab } from './profile-actions-tab';
import { ProfileMessagesTab } from './profile-messages-tab';
import { ProfileTimesTab } from './profile-times-tab';

interface ProfileDetailViewProps {
    profile: Profile;
    recipients: Recipient[];
    onUpdate: () => void;
}

export function ProfileDetailView({ profile, recipients, onUpdate }: ProfileDetailViewProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [activeTab, setActiveTab] = useState('times');
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);

    const handleToggleActive = async (active: boolean) => {
        try {
            await updateProfileStatus(profile.id, active);
            toast({
                title: t('success.title'),
                description: t('forward.profileStatusChanged', { status: active ? t('forward.active') : t('forward.inactive') })
            });
            onUpdate();
        } catch (error) {
            toast({
                title: t('error.title'),
                description: t('forward.statusChangeError'),
                variant: 'destructive'
            });
        }
    };

    const handleDeleteProfile = async () => {
        try {
            await deleteProfile(profile.id);
            toast({
                title: t('success.title'),
                description: t('forward.profileDeleted')
            });
            setShowDeleteDialog(false);
            onUpdate();
        } catch (error: any) {
            const errorMessage = error.response?.data?.errorCode === 'PROFILE_IN_USE'
                ? t('forward.profileInUseError')
                : t('forward.profileDeleteError');
            toast({
                title: t('error.title'),
                description: errorMessage,
                variant: 'destructive'
            });
        }
    };

    const handleEditProfile = async (data: { title: string; receivers: string[] }) => {
        try {
            await updateProfile(profile.id, data);
            toast({
                title: t('success.title'),
                description: t('forward.profileUpdated')
            });
            setShowEditDialog(false);
            onUpdate();
        } catch (error) {
            toast({
                title: t('error.title'),
                description: t('forward.profileUpdateError'),
                variant: 'destructive'
            });
        }
    };

    return (
        <div className="h-full overflow-y-auto bg-gray-50">
            <div className="py-6 px-6">
                {/* Header - Full Width, Dezent */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <h1 className="text-xl font-semibold text-gray-900">{profile.title}</h1>
                        <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${profile.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                {profile.active ? t('forward.active') : t('forward.inactive')}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Switch
                            checked={profile.active}
                            onCheckedChange={handleToggleActive}
                        />
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowEditDialog(true)}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowDeleteDialog(true)}
                            className="text-gray-400 hover:text-red-600"
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* Tabs Navigation - Dezent */}
                <div className="border-b border-gray-200 mb-6">
                    <div className="flex gap-6">
                        <button
                            onClick={() => setActiveTab('times')}
                            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'times'
                                ? 'border-gray-900 text-gray-900'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {t('forward.forwardingTimes')}
                        </button>
                        <button
                            onClick={() => setActiveTab('actions')}
                            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'actions'
                                ? 'border-gray-900 text-gray-900'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {t('forward.actionsTab')}
                        </button>
                        <button
                            onClick={() => setActiveTab('messages')}
                            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'messages'
                                ? 'border-gray-900 text-gray-900'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {t('forward.messagesTab')}
                        </button>
                    </div>
                </div>

                {/* Tab Content - Full Width */}
                <div>
                    {activeTab === 'times' && (
                        <ProfileTimesTab profile={profile} recipients={recipients} onUpdate={onUpdate} />
                    )}
                    {activeTab === 'actions' && (
                        <ProfileActionsTab profile={profile} onUpdate={onUpdate} />
                    )}
                    {activeTab === 'messages' && (
                        <ProfileMessagesTab profile={profile} onUpdate={onUpdate} />
                    )}
                </div>
            </div>

            <DeleteProfileDialog
                isOpen={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleDeleteProfile}
                profileName={profile.title}
            />

            <EditProfileDialog
                open={showEditDialog}
                onOpenChange={setShowEditDialog}
                profile={profile}
                recipients={recipients}
                onSave={handleEditProfile}
            />
        </div >
    );
}
